/*
  ONJ CUSTOM — onj-gift-note.js
  Purpose:  Adds/removes the GIFT NOTE pick SKU so it always matches cart.note
  Consumed: snippets/onj-gift-note.liquid (sole call site; config JSON there)
  Added:    2026-11 · Horizon migration R1
  Round 2:  Permanent while gift notes ship as a picked SKU. Retire with
            snippets/onj-gift-note.liquid (exit condition stated there).

  Rule (note is the source of truth):
    note present + other items  -> GIFT NOTE qty exactly 1
    note blank                  -> GIFT NOTE qty 0
    no other items              -> GIFT NOTE qty 0 and note cleared

  Triggers: StandardEvents.cartNoteUpdate (Horizon's cart-note.js, after its
  write resolves) and StandardEvents.cartLinesUpdate (anything else changing
  the cart). Each run re-reads /cart.js, so a missed or out-of-order event is
  corrected by the next one.

  Writes go through /cart/update.js with `updates` keyed by VARIANT ID (adds
  the line if absent, never reindexes other lines) and never send `note`
  except to clear it on an otherwise empty cart, so they can't race the
  customer's typing. The resulting CartLinesUpdateEvent carries section HTML
  so the drawer / cart page re-render; the textarea has data-skip-node-update
  and keeps its value through the morph.

  Static import in a module script on purpose: Shopify's asset minifier turns
  dynamic import() into require() (see onj-legacy-cart-reconcile.js, 2026-10-30).
*/
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const SOURCE = 'onj-gift-note';

let config;
try {
  config = JSON.parse(document.querySelector('[data-onj-gift-note]')?.textContent || 'null');
} catch (e) {
  config = null;
}

if (config && config.variantId) {
  const VARIANT = Number(config.variantId);
  const routes = (typeof Theme !== 'undefined' && Theme.routes) || {};
  const cartUrl = routes.cart_url || '/cart';

  let running = false;
  let again = false;
  let timer = null;

  const track = (action, payload = {}) => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({ event: 'onj_gift_note', action, ...payload });
  };

  const sectionIds = () =>
    Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

  const getCart = async () => {
    const r = await fetch(`${cartUrl}.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin',
      cache: 'no-store',
    });
    if (!r.ok) throw new Error(`cart ${r.status}`);
    return r.json();
  };

  // Mirror component-cart-items.js: a direct AJAX write dispatches nothing, so
  // header count, drawer and cart page would keep showing the old cart.
  const announce = (response) => {
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart',
        lines: [],
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(response),
      detail: {
        sections: response.sections,
        items: response.items,
        itemCount: response.item_count,
        source: SOURCE,
        didError: false,
      },
    });
  };

  const reconcile = async (reason) => {
    if (running) {
      again = true;
      return;
    }
    running = true;

    try {
      const cart = await getCart();
      const hasNote = String(cart.note || '').trim() !== '';
      let giftQty = 0;
      let otherQty = 0;
      for (const item of cart.items) {
        if (item.variant_id === VARIANT) giftQty += item.quantity;
        else otherQty += item.quantity;
      }

      let body = null;
      if (otherQty === 0) {
        if (giftQty > 0 || hasNote) body = { updates: { [VARIANT]: 0 }, note: '' };
      } else if (hasNote && giftQty !== 1) {
        body = { updates: { [VARIANT]: 1 } };
      } else if (!hasNote && giftQty > 0) {
        body = { updates: { [VARIANT]: 0 } };
      }
      if (!body) return;

      const r = await fetch(`${cartUrl}/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({
          ...body,
          sections: sectionIds().join(','),
          sections_url: window.location.pathname,
        }),
      });
      const response = await r.json();
      if (!r.ok || response.status) {
        // e.g. 422 if the GIFT NOTE product is unpublished or out of stock
        throw new Error(response.description || response.message || `update ${r.status}`);
      }

      track(body.updates[VARIANT] ? 'added' : 'removed', { reason });
      announce(response);
    } catch (e) {
      track('error', { reason, message: String(e && e.message) });
      console.warn('[onj-gift-note]', e);
    } finally {
      running = false;
      if (again) {
        again = false;
        schedule('queued');
      }
    }
  };

  function schedule(reason) {
    clearTimeout(timer);
    timer = setTimeout(() => reconcile(reason), 400);
  }

  // Wait for the triggering write to land before reading the cart.
  const after = (event, reason) => {
    const p = event.promise;
    if (!p) return schedule(reason);
    p.then((result) => {
      if (result && result.detail && result.detail.source === SOURCE) return;
      schedule(reason);
    }).catch(() => {});
  };

  document.addEventListener(StandardEvents.cartNoteUpdate, (e) => after(e, 'note'));
  document.addEventListener(StandardEvents.cartLinesUpdate, (e) => after(e, 'lines'));

  if (config.needsSync) reconcile('load');
}
